import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Lock, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { FloatingWhatsAppButton } from '@/components/FloatingWhatsAppButton';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const recoveryHandled = useRef(false);

  useEffect(() => {
    const url = new URL(window.location.href);

    // Supabase already rejected the token server-side before redirecting here
    if (url.searchParams.get('error')) {
      setIsRecovery(false);
      setIsChecking(false);
      return;
    }

    // PASSWORD_RECOVERY fires after a successful verifyOtp or after the client
    // auto-exchanges a ?code= param (PKCE). Either path lands here.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryHandled.current = true;
        setIsRecovery(true);
        setIsChecking(false);
        window.history.replaceState({}, '', '/reset-password');
      }
    });

    const init = async () => {
      const recoveryToken = url.searchParams.get('recovery_token');
      const recoveryEmail = url.searchParams.get('email');
      const typeParam     = url.searchParams.get('type');

      // Primary flow: email link is /reset-password?recovery_token=...&type=recovery
      // Email scanners fetch the HTML and stop — JS never runs so the token stays intact.
      // When a real user clicks, this JS verifies the token and opens the password form.
      if (recoveryToken && typeParam === 'recovery') {
        const tokenType = url.searchParams.get('recovery_token_type') === 'token_hash'
          ? 'token_hash'
          : 'token';

        const { error } = tokenType === 'token_hash'
          ? await supabase.auth.verifyOtp({ type: 'recovery', token_hash: recoveryToken })
          : recoveryEmail
            ? await supabase.auth.verifyOtp({ type: 'recovery', email: recoveryEmail, token: recoveryToken })
            : { error: new Error('El enlace de recuperación no incluye el correo requerido.') };

        if (error) {
          console.error('[reset-password] Verification error:', error.message);
          setIsRecovery(false);
          setIsChecking(false);
          return;
        }

        recoveryHandled.current = true;
        setIsRecovery(true);
        setIsChecking(false);
        window.history.replaceState({}, '', '/reset-password');
        return;
      }

      // After the Supabase redirect lands back with ?code=, the client auto-exchanges
      // it via detectSessionInUrl and fires PASSWORD_RECOVERY — nothing to do manually.
      if (url.searchParams.get('code')) return;

      // Implicit flow fallback: #type=recovery hash (older Supabase project config)
      if (window.location.hash.includes('type=recovery')) {
        recoveryHandled.current = true;
        setIsRecovery(true);
        setIsChecking(false);
        return;
      }
    };

    init();

    const timeout = setTimeout(() => {
      if (!recoveryHandled.current) setIsChecking(false);
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({ title: 'Error', description: 'Las contraseñas no coinciden.', variant: 'destructive' });
      return;
    }

    if (password.length < 6) {
      toast({ title: 'Error', description: 'La contraseña debe tener al menos 6 caracteres.', variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsLoading(false);

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      // Sign out after password update so the user logs in fresh
      await supabase.auth.signOut();
      toast({ title: '¡Contraseña actualizada!', description: 'Ya puedes iniciar sesión con tu nueva contraseña.' });
      navigate('/auth');
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verificando enlace...</p>
        </div>
        <FloatingWhatsAppButton />
      </div>
    );
  }

  if (!isRecovery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Brain className="h-8 w-8 text-primary mx-auto mb-2" />
            <CardTitle>Enlace inválido</CardTitle>
            <CardDescription>Este enlace de recuperación no es válido o ha expirado.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/auth')} className="w-full">Ir a Iniciar Sesión</Button>
          </CardContent>
        </Card>
        <FloatingWhatsAppButton />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Brain className="h-8 w-8 text-primary mx-auto mb-2" />
          <CardTitle>Nueva contraseña</CardTitle>
          <CardDescription>Ingresa tu nueva contraseña para tu cuenta.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirmar contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="confirm" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <FloatingWhatsAppButton />
    </div>
  );
};

export default ResetPasswordPage;
